import React, { useEffect, useState } from "react";
import { Box, Typography, makeStyles } from "@material-ui/core";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import AppDialog from "components/Dialog/AppDialog";
import AppButton from "components/CustomButtons/AppButton";
import AppInput from "components/Input/AppInput";
import SelectField from "components/Select/SelectField";
import CustomDatePicker from "components/Input/CustomDatePicker";
import AppColor from "utilities/AppColor";
import { RISK_CATEGORY, RISK_SEVERITY, RISK_LIKELIHOOD, RISK_STATUS } from "config/AppConfig";
import { createOrUpdateRisk, fetchRiskOwners } from "store/slices/client";
import moment from "moment";

// ── Risk rating matrix (mirrors backend logic) ────────────────────────────────
const RISK_RATING_MATRIX = {
  Almost_certain: { Extreme: "Extreme", High: "Extreme", Medium: "High", Low: "Medium" },
  Likely:         { Extreme: "Extreme", High: "High",    Medium: "High", Low: "Low"    },
  Possible:       { Extreme: "High",    High: "High",    Medium: "Medium", Low: "Low"  },
  Unlikely:       { Extreme: "High",    High: "Medium",  Medium: "Low",  Low: "Low"    },
  Rare:           { Extreme: "Medium",  High: "Low",     Medium: "Low",  Low: "Low"    },
};

const RATING_COLORS = {
  Extreme: { bg: "#FEE2E2", color: "#991B1B", border: "#FCA5A5" },
  High:    { bg: "#FFEDD5", color: "#9A3412", border: "#FED7AA" },
  Medium:  { bg: "#FEF9C3", color: "#854D0E", border: "#FDE68A" },
  Low:     { bg: "#D1FAE5", color: "#065F46", border: "#6EE7B7" },
};

const useStyles = makeStyles(() => ({
  sectionTitle: {
    fontSize: 13,
    fontWeight: 800,
    color: "#0D9488",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    margin: "10px 0 15px",
    display: "block",
  },
  formRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 15,
    marginBottom: 10,
  },
  formRowTriple: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr",
    gap: 15,
    marginBottom: 10,
  },
  ratingReadonly: {
    background: "#F9FAFB",
    border: "1px solid #E5E7EB",
    borderRadius: 6,
    padding: "10px 14px",
    fontSize: 14,
    color: "#6B7280",
    minHeight: 40,
    display: "flex",
    alignItems: "center",
    marginBottom: "1rem",
    maxHeight: 42,
  },
  ratingBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "4px 12px",
    borderRadius: 20,
    fontSize: 13,
    fontWeight: 700,
    border: "1px solid",
  },
  ratingLabel: {
    fontSize: 14,
    color: "#000",
    fontWeight: 500,
    marginBottom: "0.5rem",
  },
  control: {
    display: "flex",
    flexWrap: "wrap",
    flexDirection: "row",
    justifyContent: "flex-end",
    marginTop: 20,
    marginBottom: 10,
    gap: 8,
  },
}));

const AddRiskModal = ({ open, onClose, clientId, companyId, participantName, onSuccess, initialData }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [ownerOptions, setOwnerOptions] = useState([]);

  useEffect(() => {
    if (open && companyId) {
      dispatch(fetchRiskOwners({ companyId })).then((result) => {
        if (result.payload && Array.isArray(result.payload)) {
          setOwnerOptions(
            result.payload.map((u) => ({
              value: u.id,
              label: `${u.firstName || ""} ${u.lastName || ""}`.trim(),
            }))
          );
        }
      });
    }
  }, [open, companyId, dispatch]);

  const isEditMode = Boolean(initialData?.id);

  const categoryOptions = [
    { value: RISK_CATEGORY.BEHAVIOUR, label: t("riskCategory.Behaviour") },
    { value: RISK_CATEGORY.MEDICAL, label: t("riskCategory.Medical") },
    { value: RISK_CATEGORY.PHYSICAL, label: t("riskCategory.Physical") },
    { value: RISK_CATEGORY.ENVIRONMENTAL, label: t("riskCategory.Environmental") },
    { value: RISK_CATEGORY.COMMUNITY, label: t("riskCategory.Community") },
    { value: RISK_CATEGORY.FINANCIAL, label: t("riskCategory.Financial") },
    { value: RISK_CATEGORY.OTHER, label: t("riskCategory.Other") },
  ];

  const severityOptions = [
    { value: RISK_SEVERITY.EXTREME, label: t("riskSeverity.Extreme") },
    { value: RISK_SEVERITY.HIGH, label: t("riskSeverity.High") },
    { value: RISK_SEVERITY.MEDIUM, label: t("riskSeverity.Medium") },
    { value: RISK_SEVERITY.LOW, label: t("riskSeverity.Low") },
  ];

  const likelihoodOptions = [
    { value: RISK_LIKELIHOOD.ALMOST_CERTAIN, label: t("riskLikelihood.Almost_certain") },
    { value: RISK_LIKELIHOOD.LIKELY, label: t("riskLikelihood.Likely") },
    { value: RISK_LIKELIHOOD.POSSIBLE, label: t("riskLikelihood.Possible") },
    { value: RISK_LIKELIHOOD.UNLIKELY, label: t("riskLikelihood.Unlikely") },
    { value: RISK_LIKELIHOOD.RARE, label: t("riskLikelihood.Rare") },
  ];

  const statusOptions = [
    { value: RISK_STATUS.ACTIVE, label: t("riskStatus.Active") },
    { value: RISK_STATUS.RESOLVED, label: t("riskStatus.Resolved") },
    { value: RISK_STATUS.MONITORING, label: t("riskStatus.Monitoring") },
  ];

  const initialValues = {
    name: initialData?.name || "",
    category: initialData?.category || "",
    severity: initialData?.severity || "",
    likelihood: initialData?.likelihood || "",
    description: initialData?.description || "",
    currentControls: initialData?.currentControls || "",
    ownerId: initialData?.ownerId || "",
    reviewDate: initialData?.reviewDate ? moment(initialData.reviewDate).format("YYYY/MM/DD") : "",
    status: initialData?.status || RISK_STATUS.ACTIVE,
    linkedParticipant: initialData?.linkedParticipant || participantName || "",
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string().trim().required(t("validation.required")).max(255, t("validation.max_length")),
    category: Yup.string().required(t("validation.required")),
    severity: Yup.string().required(t("validation.required")),
    likelihood: Yup.string().required(t("validation.required")),
  });

  const handleSubmit = async (values, { setSubmitting }) => {
    const payload = {
      companyId,
      clientId,
      name: values.name.trim(),
      category: values.category,
      severity: values.severity,
      likelihood: values.likelihood,
      description: values.description || null,
      currentControls: values.currentControls || null,
      ownerId: values.ownerId ? Number(values.ownerId) : null,
      reviewDate: values.reviewDate || null,
      status: values.status || RISK_STATUS.ACTIVE,
    };

    if (isEditMode) {
      payload.id = initialData.id;
    }

    const result = await dispatch(createOrUpdateRisk(payload));
    setSubmitting(false);

    if (createOrUpdateRisk.fulfilled.match(result)) {
      onSuccess && onSuccess();
      onClose();
    }
  };

  const computeRating = (severity, likelihood) => {
    if (!severity || !likelihood) return null;
    return RISK_RATING_MATRIX[likelihood]?.[severity] || null;
  };

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      title={isEditMode ? t("riskConsent.editRisk") : t("riskConsent.addRisk")}
      maxWidth="md"
    >
      <Box >
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ values, isSubmitting }) => {
            const computedRating = computeRating(values.severity, values.likelihood);
            const ratingStyle = computedRating ? RATING_COLORS[computedRating] : null;

            return (
              <Form>
                {/* ── Section: Risk Identification ── */}
                <Typography className={classes.sectionTitle}>
                  {t("riskConsent.sectionIdentification")}
                </Typography>

                <div className="row">
                  <div className="col-sm-12 col-md-12">
                    <Field
                      component={AppInput}
                      name="name"
                      label="riskConsent.riskName"
                      placeholder="riskConsent.riskNamePlaceholder"
                      required
                    />
                  </div>
                  <div className="col-sm-12 col-md-6">
                    <Field
                      component={SelectField}
                      name="category"
                      label="riskConsent.riskCategory"
                      placeholder="riskConsent.selectCategory"
                      options={categoryOptions}
                      required
                    />
                  </div>
                  <div className="col-sm-12 col-md-6">
                    <Field
                      component={SelectField}
                      name="status"
                      label="riskConsent.status"
                      options={statusOptions}
                    />
                  </div>
                </div>

                {/* ── Section: Risk Assessment ── */}
                <Typography className={classes.sectionTitle}>
                  {t("riskConsent.sectionAssessment")}
                </Typography>

                <div className="row">
                  <div className="col-sm-12 col-md-4">
                    <Field
                      component={SelectField}
                      name="severity"
                      label="riskConsent.severity"
                      placeholder="riskConsent.selectSeverity"
                      options={severityOptions}
                      required
                    />
                  </div>
                  <div className="col-sm-12 col-md-4">
                    <Field
                      component={SelectField}
                      name="likelihood"
                      label="riskConsent.likelihood"
                      placeholder="riskConsent.selectLikelihood"
                      options={likelihoodOptions}
                      required
                    />
                  </div>
                  <div className="col-sm-12 col-md-4">
                    <Box className={classes.ratingLabel}>{t("riskConsent.riskRating")}</Box>
                    <Box
                      className={classes.ratingReadonly}
                      style={computedRating && ratingStyle
                        ? {
                          background: ratingStyle.bg,
                          color: ratingStyle.color,
                          borderColor: ratingStyle.border,
                        }
                        : {}
                      }
                    >
                      {computedRating && ratingStyle ? (
                        <span >
                          {t(`riskRating.${computedRating}`)}
                        </span>
                      ) : (
                        <span style={{ color: "#9CA3AF", fontSize: 13 }}>
                          {t("riskConsent.selectSeverityAndLikelihood")}
                        </span>
                      )}
                    </Box>
                  </div>
                </div>

                {/* ── Section: Risk Detail ── */}
                <Typography className={classes.sectionTitle}>
                  {t("riskConsent.sectionDetail")}
                </Typography>

                <div className="row">
                  <div className="col-sm-12">
                    <Field
                      component={AppInput}
                      name="description"
                      label="riskConsent.description"
                      placeholder="riskConsent.descriptionPlaceholder"
                      type="textarea"
                      rows={3}
                    />
                  </div>
                  <div className="col-sm-12">
                    <Field
                      component={AppInput}
                      name="currentControls"
                      label="riskConsent.currentControls"
                      placeholder="riskConsent.currentControlsPlaceholder"
                      type="textarea"
                      rows={3}
                    />
                  </div>
                </div>

                {/* ── Section: Management ── */}
                <Typography className={classes.sectionTitle}>
                  {t("riskConsent.sectionManagement")}
                </Typography>

                <div className="row">
                  <div className="col-sm-12 col-md-6">
                    <Field
                      component={SelectField}
                      name="ownerId"
                      label="riskConsent.owner"
                      placeholder="riskConsent.selectOwner"
                      options={ownerOptions}
                    />
                  </div>
                  <div className="col-sm-12 col-md-6">
                    <Field
                      component={CustomDatePicker}
                      name="reviewDate"
                      label="riskConsent.reviewDate"
                      formatDateJob={true}
                    />
                  </div>
                  <div className="col-sm-12 col-md-6">
                    <Field
                      component={AppInput}
                      name="linkedParticipant"
                      label="riskConsent.linkedParticipant"
                    />
                  </div>
                </div>

                {/* ── Actions ── */}
                <Box className={classes.control}>
                  <AppButton
                    label={t("button.cancel")}
                    onClick={onClose}
                    buttonColor="#F3F4F6"
                    rounder
                    style={{ color: "#111827" }}
                    disabled={isSubmitting}
                  />
                  <AppButton
                    label={isEditMode ? t("button.save_changes") : t("riskConsent.saveRisk")}
                    rounder
                    type="submit"
                    isLoading={isSubmitting}
                    disabled={isSubmitting}
                    buttonColor={AppColor.mainColor}
                  />
                </Box>
              </Form>
            );
          }}
        </Formik>
      </Box>
    </AppDialog>
  );
};

export default AddRiskModal;
